import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Hash, Plus, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, type SellerShop, type UgcArticle, type UgcArticleStatus, type UgcOwnerKind } from "@/api";
import { DataTablePagination } from "@/components/data-table";
import { ProductPicker } from "@/components/product-picker";
import { num, PeriodFilter, type Period, rub, StatTile } from "@/components/promo-kit";
import { UGC_CHANNEL_LABEL, UGC_COMMISSION_LABEL, UGC_OWNER_LABEL, UGC_ROLE_LABEL, UGC_STATUS_LABEL, UgcArticleDialog, UgcOwnerBadge, UgcStatusBadge } from "@/components/ugc-kit";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { useCanManagePromos } from "./promos";

// Ключи фильтров с префиксом u*: вкладка живёт на /promos рядом с
// «Промокодами», у которых свой ?partner= (партнёр промокода, не UGC).
const FILTERS = ["uq", "ushop", "upartner", "uproduct", "ustatus", "uowner"] as const;
const SORT_KEYS = ["created"] as const;
const selectClass = "h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm";

/**
 * «UGC-Артикулы» (ТЗ 31.10.2026, 4.7): реестр всех цифровых артикулов
 * площадки - продавцов, партнёров и платформенных. Сводка за период сверху,
 * поиск по номеру, продавцу и партнёру, фильтры, серверная пагинация.
 * Строка открывает карточку артикула, там - действия и выплаты.
 */
export function UgcArticlesTab() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canManage = useCanManagePromos();
  const url = useUrlTable<(typeof SORT_KEYS)[number]>({
    filters: FILTERS,
    defaultSort: {
      key: "created",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: SORT_KEYS
  });
  const f = url.filters;
  const period: Period = {
    from: params.get("ufrom") ?? "",
    to: params.get("uto") ?? ""
  };
  const setPeriod = (p: Period) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (p.from) next.set("ufrom", p.from);else next.delete("ufrom");
    if (p.to) next.set("uto", p.to);else next.delete("uto");
    return next;
  }, {
    replace: true
  });
  const summaryQ = useQuery({
    queryKey: ["ugc", "summary", period.from, period.to],
    queryFn: () => api.ugc.summary({
      from: period.from || undefined,
      to: period.to || undefined
    })
  });
  const listQ = useQuery({
    queryKey: ["ugc", "list", f, url.page, url.pageSize],
    queryFn: () => api.ugc.list({
      q: f.uq || undefined,
      shopId: f.ushop || undefined,
      partnerId: f.upartner || undefined,
      productId: f.uproduct || undefined,
      status: (f.ustatus || undefined) as UgcArticleStatus | undefined,
      ownerKind: (f.uowner || undefined) as UgcOwnerKind | undefined,
      page: url.page,
      pageSize: url.pageSize
    }),
    placeholderData: prev_0 => prev_0
  });
  const shopsQ = useQuery({
    queryKey: ["sellers", "list"],
    queryFn: () => api.sellers.list()
  });
  const shops = (shopsQ.data ?? []).filter((s: SellerShop) => s.shopId).map((s_0: SellerShop) => ({
    id: s_0.shopId as string,
    name: s_0.shopName || s_0.sellerName
  }));
  const items = listQ.data?.items ?? [];
  const table = serverDataTable(url, items, listQ.data?.total ?? 0);

  // Поиск с задержкой: адрес и запрос меняются, когда человек перестал печатать.
  const [search, setSearch] = useState(f.uq ?? "");
  const urlSearch = f.uq ?? "";
  useEffect(() => setSearch(urlSearch), [urlSearch]);
  const setFilterRef = useRef(url.setFilter);
  setFilterRef.current = url.setFilter;
  useEffect(() => {
    if (search.trim() === urlSearch) return;
    const id = setTimeout(() => setFilterRef.current("uq", search.trim()), 300);
    return () => clearTimeout(id);
  }, [search, urlSearch]);

  // Имя партнёра для плашки фильтра: отдельного списка UGC-партнёров у
  // консоли нет, имя берём из строк (фильтр ставится кликом по партнёру).
  const [partnerName, setPartnerName] = useState<string>("");
  const partnerLabel = items.find(a => a.partnerId === f.upartner)?.partnerName ?? (partnerName || f.upartner);
  const [creating, setCreating] = useState(false);
  const open = (a_0: UgcArticle) => navigate(`/promos/ugc/${encodeURIComponent(a_0.id)}`);
  const filterBy = (patch: Record<string, string>) => (e: React.MouseEvent) => {
    e.stopPropagation();
    url.setFilters(patch);
  };
  const s_1 = summaryQ.data;
  const totals = s_1?.totals;
  const balance = s_1?.balance;
  const error = listQ.error ? (listQ.error as Error).message : null;
  const hasAnyFilter = url.hasFilters;
  return <div className="flex min-w-0 flex-col gap-5" data-testid="ugc-tab">
			<div className="flex flex-col gap-3" data-tour="ugc-summary">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<PeriodFilter value={period} onChange={setPeriod} resolved={s_1 ? {
          from: s_1.from,
          to: s_1.to
        } : undefined} />
					{canManage && <Button onClick={() => setCreating(true)} data-testid="ugc-create" data-tour="ugc-create">
							<Plus className="mr-2 size-4" />
							Платформенный артикул
						</Button>}
				</div>
				{summaryQ.error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{(summaryQ.error as Error).message}</p>}
				<div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="ugc-summary">
					<StatTile label="Переходы" value={totals ? num(totals.clicks) : "…"} hint={totals ? `CR ${num(totals.cr)} %` : undefined} />
					<StatTile label="Заказы" value={totals ? num(totals.orders) : "…"} hint={totals ? `оплачено ${num(totals.paidOrders)} · выкуплено ${num(totals.completedOrders)}` : undefined} />
					<StatTile label="GMV" value={totals ? rub(totals.gmv) : "…"} hint={totals ? `возвратов и отмен: ${num(totals.returns)}` : undefined} />
					<StatTile label="Комиссия партнёрам" value={totals ? rub(totals.commission) : "…"} hint="HOLD + к выплате + выплачено" />
				</div>
				<div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="ugc-balance">
					{(["hold", "available", "paid", "cancelled"] as const).map(k => <StatTile key={k} label={UGC_COMMISSION_LABEL[k]} value={balance ? rub(balance[k]) : totals ? "—" : "…"} hint={k === "hold" ? "заказ оформлен, оплачен или в пути" : k === "available" ? "завершён + 14 дней на возврат" : k === "paid" ? "отмечено в карточке артикула" : "отмена, возврат, самопокупка, блокировка"} />)}
				</div>
			</div>

			<div className="flex flex-col gap-2" data-tour="ugc-filters">
				<div className="flex flex-wrap items-center gap-2">
					<div className="relative min-w-[220px] flex-1">
						<Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
						<Input className="pl-8" inputMode="search" placeholder="Артикул, продавец, партнёр, товар" value={search} onChange={e_0 => setSearch(e_0.target.value)} data-testid="ugc-search" />
					</div>
					<div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
						<select className={selectClass} value={f.ustatus ?? ""} onChange={e_1 => url.setFilter("ustatus", e_1.target.value)} aria-label="Статус" data-testid="ugc-filter-status">
							<option value="">Все статусы</option>
							{(Object.keys(UGC_STATUS_LABEL) as UgcArticleStatus[]).map(k_0 => <option key={k_0} value={k_0}>
									{UGC_STATUS_LABEL[k_0]}
								</option>)}
						</select>
						<select className={selectClass} value={f.uowner ?? ""} onChange={e_2 => url.setFilter("uowner", e_2.target.value)} aria-label="Владелец" data-testid="ugc-filter-owner">
							<option value="">Все владельцы</option>
							{(Object.keys(UGC_OWNER_LABEL) as UgcOwnerKind[]).map(k_1 => <option key={k_1} value={k_1}>
									{UGC_OWNER_LABEL[k_1]}
								</option>)}
						</select>
						<select className={`${selectClass} col-span-2 sm:max-w-[220px]`} value={f.ushop ?? ""} onChange={e_3 => url.setFilter("ushop", e_3.target.value)} aria-label="Продавец" data-testid="ugc-filter-shop">
							<option value="">Все продавцы</option>
							{f.ushop && !shops.some(x => x.id === f.ushop) && <option value={f.ushop ?? ""}>{items[0]?.shopName ?? f.ushop}</option>}
							{shops.map(x_0 => <option key={x_0.id} value={x_0.id}>
									{x_0.name}
								</option>)}
						</select>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<div className="flex w-full min-w-0 sm:w-80" data-testid="ugc-filter-product">
						<ProductPicker value={f.uproduct ?? ""} onChange={id_0 => url.setFilter("uproduct", id_0)} placeholder="Фильтр по товару" />
					</div>
					{f.upartner && <span className="inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/50 py-0.5 pl-3 pr-1 text-sm" data-testid="ugc-filter-partner">
							<span className="truncate">Партнёр: {partnerLabel}</span>
							<button type="button" className="rounded-full p-1 hover:bg-muted" aria-label="Снять фильтр по партнёру" onClick={() => url.setFilter("upartner", "")}>
								<X className="size-3.5" />
							</button>
						</span>}
					{hasAnyFilter && <Button variant="ghost" size="sm" onClick={() => {
          setSearch("");
          url.resetFilters();
        }} data-testid="ugc-reset">
							Сбросить фильтры
						</Button>}
				</div>
			</div>

			{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

			<div data-tour="ugc-table" className="flex min-w-0 flex-col gap-3">
				{/* Телефон: карточки вместо широкой таблицы. */}
				<div className="flex flex-col gap-2 md:hidden" data-testid="ugc-cards">
					{listQ.isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Загружаем…</p>}
					{!listQ.isLoading && items.length === 0 && <Empty filtered={hasAnyFilter} />}
					{items.map(a_1 => <button key={a_1.id} type="button" onClick={() => open(a_1)} className="flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-3 text-left" data-testid={`ugc-card-${a_1.code}`}>
							<div className="flex items-center justify-between gap-2">
								<span className="font-mono text-base font-semibold tabular-nums">{a_1.code}</span>
								<UgcStatusBadge status={a_1.status} />
							</div>
							<div className="min-w-0 text-sm">
								<div className="truncate font-medium">{a_1.productTitle}</div>
								<div className="truncate text-xs text-muted-foreground">
									{a_1.shopName} · {a_1.name}
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
								<UgcOwnerBadge owner={a_1.ownerKind} />
								{a_1.partnerName && <span className="truncate">{a_1.partnerName}</span>}
								<span>· {UGC_CHANNEL_LABEL[a_1.channel]}</span>
							</div>
							<div className="grid grid-cols-4 gap-1 text-xs">
								<Metric label="Переходы" value={num(a_1.metrics.clicks)} />
								<Metric label="Заказы" value={num(a_1.metrics.orders)} />
								<Metric label="GMV" value={rub(a_1.metrics.gmv)} />
								<Metric label="Комиссия" value={a_1.ownerKind === "partner" ? rub(a_1.metrics.commission) : "—"} />
							</div>
						</button>)}
				</div>

				<div className="hidden overflow-x-auto rounded-lg border md:block">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Артикул</TableHead>
								<TableHead>Товар и магазин</TableHead>
								<TableHead>Владелец</TableHead>
								<TableHead>Канал</TableHead>
								<TableHead>Статус</TableHead>
								<TableHead className="text-right">Переходы</TableHead>
								<TableHead className="text-right">Заказы</TableHead>
								<TableHead className="text-right">GMV</TableHead>
								<TableHead className="text-right">Комиссия</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{listQ.isLoading && <TableRow>
									<TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
										Загружаем…
									</TableCell>
								</TableRow>}
							{!listQ.isLoading && items.length === 0 && <TableRow>
									<TableCell colSpan={9}>
										<Empty filtered={hasAnyFilter} />
									</TableCell>
								</TableRow>}
							{items.map(a_2 => <TableRow key={a_2.id} className="cursor-pointer" onClick={() => open(a_2)} data-testid={`ugc-row-${a_2.code}`}>
									<TableCell>
										<div className="font-mono font-medium tabular-nums">{a_2.code}</div>
										<div className="max-w-[150px] truncate text-xs text-muted-foreground" title={a_2.name}>
											{a_2.name}
										</div>
									</TableCell>
									<TableCell className="max-w-[210px]">
										<button type="button" className="block max-w-full truncate text-left hover:underline" title={`Только артикулы товара «${a_2.productTitle}»`} onClick={filterBy({
                  uproduct: a_2.productId
                })}>
											{a_2.productTitle}
										</button>
										<button type="button" className="block max-w-full truncate text-left text-xs text-muted-foreground hover:underline" title={`Только магазин «${a_2.shopName}»`} onClick={filterBy({
                  ushop: a_2.shopId
                })}>
											{a_2.shopName}
										</button>
									</TableCell>
									<TableCell className="max-w-[170px]">
										<UgcOwnerBadge owner={a_2.ownerKind} />
										{a_2.partnerId && <button type="button" className="mt-0.5 block max-w-full truncate text-left text-xs hover:underline" title="Только артикулы этого партнёра" onClick={e_4 => {
                  setPartnerName(a_2.partnerName ?? "");
                  filterBy({
                    upartner: a_2.partnerId as string
                  })(e_4);
                }}>
												{a_2.partnerName}
												{a_2.partnerRole && <span className="text-muted-foreground"> · {UGC_ROLE_LABEL[a_2.partnerRole]}</span>}
											</button>}
									</TableCell>
									<TableCell className="max-w-[160px]">
										<div className="text-sm">{UGC_CHANNEL_LABEL[a_2.channel]}</div>
										{(a_2.placement || a_2.campaign) && <div className="truncate text-xs text-muted-foreground" title={[a_2.placement, a_2.campaign].filter(Boolean).join(" · ")}>
												{[a_2.placement, a_2.campaign].filter(Boolean).join(" · ")}
											</div>}
									</TableCell>
									<TableCell>
										<UgcStatusBadge status={a_2.status} />
									</TableCell>
									<TableCell className="text-right tabular-nums">{num(a_2.metrics.clicks)}</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">
										{num(a_2.metrics.orders)}
										<div className="text-xs text-muted-foreground">CR {num(a_2.metrics.cr)} %</div>
									</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">{rub(a_2.metrics.gmv)}</TableCell>
									<TableCell className="whitespace-nowrap text-right tabular-nums">
										{a_2.ownerKind === "partner" ? <>
												{rub(a_2.metrics.commission)}
												<div className="text-xs text-muted-foreground">{num(a_2.commissionPercent)} %</div>
											</> : <span className="text-muted-foreground">—</span>}
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>
				</div>

				{(listQ.data?.total ?? 0) > 0 && <DataTablePagination table={table} />}
			</div>

			{creating && <UgcArticleDialog open onClose={() => setCreating(false)} onSaved={() => void queryClient.invalidateQueries({
      queryKey: ["ugc"]
    })} onOpenCard={a_3 => {
      setCreating(false);
      open(a_3);
    }} />}
		</div>;
}
function Metric(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="truncate font-medium tabular-nums">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Empty(t0) {
  const $ = _c(3);
  const {
    filtered
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Hash className="size-6" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = filtered ? "\u041F\u043E\u0434 \u0444\u0438\u043B\u044C\u0442\u0440 \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043F\u043E\u0434\u043E\u0448\u043B\u043E" : "\u0410\u0440\u0442\u0438\u043A\u0443\u043B\u043E\u0432 \u043D\u0430 \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0435 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442. \u0418\u0445 \u0441\u043E\u0437\u0434\u0430\u044E\u0442 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u044B \u0438 \u043F\u0430\u0440\u0442\u043D\u0451\u0440\u044B, \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0430 - \u043A\u043D\u043E\u043F\u043A\u043E\u0439 \xAB\u041F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0435\u043D\u043D\u044B\u0439 \u0430\u0440\u0442\u0438\u043A\u0443\u043B\xBB.";
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">{t1}{t2}</div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}